import { rem } from 'polished'
import type { MouseEvent } from 'react'
import { useCallback } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import TextLink from 'LuxKit/TextLink'
import Tooltip from 'LuxKit/Tooltip'
import Caption from 'LuxKit/Typography/Caption'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

/** Matches the offer pages' breadcrumb list, which truncates at the same width. */
const TEXT_LIMITER = 16

export interface OfferDetailBreadcrumbPlace {
  name: string
  country?: string
  /** LE place id, when the trail resolved one. Without it the listing drawer
   *  it opens has only the name to search on. */
  placeId?: string
}

export interface OfferDetailBreadcrumb {
  text: string
  /**
   * The place this crumb narrows to. A crumb without one is not actionable:
   * the leading vertical ("Hotels") has no trip planner surface of its own,
   * and the trailing crumb is the offer whose drawer is already open.
   */
  place?: OfferDetailBreadcrumbPlace
}

/**
 * Breadcrumb row at the top of an offer drawer's detail content.
 *
 * Unlike the offer pages' trail, which these crumbs are modelled on, nothing
 * here is a link. A breadcrumb click has to keep the customer inside the trip
 * planner, so a place crumb opens that place's listing drawer over the current
 * one instead of navigating the page to /search.
 *
 * The row is desktop-only, per the design; `overflow: hidden` keeps a long
 * trail from widening the drawer, which is a resizable panel and regularly far
 * narrower than the viewport the media query is measuring.
 */
const BreadcrumbsRow = styled.ul`
  display: none;
  list-style-type: none;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  align-items: center;
  width: 100%;

  ${mediaQueryUp.tablet} {
    display: flex;
  }

  .tooltip_content {
    padding-block: ${rem(4)};
    padding-inline: ${rem(8)};
  }

  > * + * {
    margin-inline-start: ${rem(4)};
  }

  > li {
    display: inline-flex;
    align-items: center;

    > * + * {
      margin-inline-start: ${rem(4)};
    }
  }
`

const CrumbLink = styled(TextLink)`
  display: block;
  white-space: nowrap;
`

function truncateString(value: string, maxLength: number = TEXT_LIMITER): string {
  if (value.length > maxLength) {
    return `${value.slice(0, maxLength)}...`
  }
  return value
}

interface Props {
  items: Array<OfferDetailBreadcrumb>
  /** Which listing drawer a place crumb should open. */
  vertical: 'hotel' | 'experience'
}

function OfferDetailBreadcrumbs({ items, vertical }: Props) {
  const eventBus = useTripPlannerEventBusOptional()

  const handleSelectPlace = useCallback((event: MouseEvent<HTMLElement>) => {
    const index = Number(event.currentTarget.dataset.crumbIndex)
    const place = items[index]?.place
    if (!place) return

    eventBus?.dispatch({
      type: 'open_listing_search_drawer',
      vertical,
      placeName: place.name,
      placeCountry: place.country,
      placeId: place.placeId,
    })
  }, [eventBus, items, vertical])

  if (items.length === 0) {
    return null
  }

  return (
    <BreadcrumbsRow>
      {items.map((item, index) => {
        const text = truncateString(item.text)
        const isTruncated = text !== item.text
        const crumb = item.place ? (
          <CrumbLink
            data-crumb-index={index}
            title={item.text}
            weight="regular"
            size="caption"
            onClick={handleSelectPlace}
          >
            {text}
          </CrumbLink>
        ) : (
          <Caption wrap="no-wrap" variant="medium" title={item.text}>
            {text}
          </Caption>
        )

        return (
          <li key={`${item.text}-${index}`}>
            {index > 0 && <LineAngleRightIcon size="XS" />}
            {isTruncated ? (
              <Tooltip placement="bottom" description={item.text}>
                {crumb}
              </Tooltip>
            ) : (
              crumb
            )}
          </li>
        )
      })}
    </BreadcrumbsRow>
  )
}

export default OfferDetailBreadcrumbs
